import { useRef, useCallback, useEffect, useMemo } from 'react';

export interface UsePipelineScrollRefs {
  stageContainerRefs: React.MutableRefObject<Map<number, HTMLDivElement>>;
  stageSectionRefs: React.MutableRefObject<Map<number, HTMLDivElement>>;
  customerCardRefs: React.MutableRefObject<Map<number, HTMLDivElement>>;
}

export interface UsePipelineScrollParams {
  stageScrollPositions: Record<number, number>;
  updateScrollPosition: (stageId: number, position: number) => void;
  prefersReducedMotion: boolean;
}

export interface UsePipelineScrollReturn {
  refs: UsePipelineScrollRefs;
  getStageContainerRef: (stageId: number) => (node: HTMLDivElement | null) => void;
  handleStageScroll: (stageId: number, event: React.UIEvent<HTMLDivElement>) => void;
  scrollStageIntoView: (stageId: number) => void;
  scrollCardIntoView: (cardId: number, stageId: number | null) => void;
}

/**
 * Hook to manage pipeline scroll behavior and refs
 * Handles scroll position persistence and smooth scrolling
 */
export function usePipelineScroll({
  stageScrollPositions,
  updateScrollPosition,
  prefersReducedMotion,
}: UsePipelineScrollParams): UsePipelineScrollReturn {
  // Refs for DOM elements
  const stageContainerRefs = useRef(new Map<number, HTMLDivElement>());
  const stageSectionRefs = useRef(new Map<number, HTMLDivElement>());
  const customerCardRefs = useRef(new Map<number, HTMLDivElement>());

  // Internal tracking refs
  const appliedScrollRefs = useRef(new Set<number>());
  const pendingScrollFrame = useRef<Record<number, number>>({});
  const stageRefCallbacks = useRef(new Map<number, (node: HTMLDivElement | null) => void>());
  const phase2TimeoutRef = useRef<number | null>(null);

  // Restore scroll positions when stage containers mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    stageContainerRefs.current.forEach((node, stageId) => {
      if (!node) return;
      const stored = stageScrollPositions[stageId];
      if (typeof stored !== 'number') return;
      if (appliedScrollRefs.current.has(stageId)) return;

      if (Math.abs(node.scrollTop - stored) > 1) {
        node.scrollTop = stored;
      }
      appliedScrollRefs.current.add(stageId);
    });
  }, [stageScrollPositions]);

  // Cleanup pending scroll frames
  useEffect(() => {
    return () => {
      if (typeof window === 'undefined') return;
      Object.values(pendingScrollFrame.current).forEach((frameId) => {
        if (frameId) {
          window.cancelAnimationFrame(frameId);
        }
      });
    };
  }, []);

  // Get ref callback for stage container (memoized per stage)
  const getStageContainerRef = useCallback((stageId: number) => {
    if (!stageRefCallbacks.current.has(stageId)) {
      const callback = (node: HTMLDivElement | null) => {
        if (node) {
          stageContainerRefs.current.set(stageId, node);
        } else {
          stageContainerRefs.current.delete(stageId);
          appliedScrollRefs.current.delete(stageId);
          stageRefCallbacks.current.delete(stageId);
        }
      };
      stageRefCallbacks.current.set(stageId, callback);
    }
    return stageRefCallbacks.current.get(stageId)!;
  }, []);

  // Handle scroll events with debounce
  const handleStageScroll = useCallback(
    (stageId: number, event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      if (!target) return;

      if (typeof window !== 'undefined') {
        const pendingFrame = pendingScrollFrame.current[stageId];
        if (pendingFrame) {
          window.cancelAnimationFrame(pendingFrame);
        }

        pendingScrollFrame.current[stageId] = window.requestAnimationFrame(() => {
          updateScrollPosition(stageId, target.scrollTop);
        });
      } else {
        updateScrollPosition(stageId, target.scrollTop);
      }
    },
    [updateScrollPosition]
  );

  // Scroll stage section into view
  const scrollStageIntoView = useCallback(
    (stageId: number) => {
      const container = stageSectionRefs.current.get(stageId);
      if (!container) return;

      container.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    },
    [prefersReducedMotion]
  );

  // Scroll card into view (two-phase approach)
  const scrollCardIntoView = useCallback(
    (cardId: number, stageId: number | null) => {
      const cardElement = customerCardRefs.current.get(cardId);
      if (!cardElement) return;

      const stageContainer = stageId ? stageContainerRefs.current.get(stageId) : null;
      const stageSection = stageId ? stageSectionRefs.current.get(stageId) : null;

      // Dynamic header height calculation
      const headerElement = document.querySelector('.sticky.top-0.z-50');
      const headerHeight = headerElement
        ? headerElement.getBoundingClientRect().height + 20
        : 180;
      const bottomBuffer = 100;

      // PHASE 1: Ensure stage section is visible
      if (stageSection) {
        const sectionRect = stageSection.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (sectionRect.top < headerHeight || sectionRect.bottom > viewportHeight - bottomBuffer) {
          stageSection.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'center',
          });
        }
      }

      // PHASE 2: Scroll card within stage panel (delayed)
      const phase2Delay = prefersReducedMotion ? 50 : 200;
      if (phase2TimeoutRef.current) {
        clearTimeout(phase2TimeoutRef.current);
      }

      phase2TimeoutRef.current = window.setTimeout(() => {
        if (!stageContainer || !cardElement) return;

        const containerRect = stageContainer.getBoundingClientRect();
        const cardRect = cardElement.getBoundingClientRect();

        const cardTopInContainer = cardRect.top - containerRect.top + stageContainer.scrollTop;
        const cardBottomInContainer = cardRect.bottom - containerRect.top + stageContainer.scrollTop;
        const containerVisibleHeight = stageContainer.clientHeight;

        const visibleTop = stageContainer.scrollTop;
        const visibleBottom = stageContainer.scrollTop + containerVisibleHeight;

        const isTopHidden = cardTopInContainer < visibleTop;
        const isBottomHidden = cardBottomInContainer > visibleBottom;

        if (isTopHidden || isBottomHidden) {
          let targetScrollTop: number;

          if (isBottomHidden) {
            targetScrollTop = cardBottomInContainer - containerVisibleHeight;
          } else {
            targetScrollTop = cardTopInContainer;
          }

          const maxScroll = stageContainer.scrollHeight - containerVisibleHeight;
          targetScrollTop = Math.max(0, Math.min(targetScrollTop, maxScroll));

          stageContainer.scrollTo({
            top: targetScrollTop,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
        }
      }, phase2Delay);
    },
    [prefersReducedMotion]
  );

  const refs = useMemo(
    () => ({ stageContainerRefs, stageSectionRefs, customerCardRefs }),
    [] // refs are useRef objects — stable for the lifetime of the component
  );

  return useMemo(
    () => ({
      refs,
      getStageContainerRef,
      handleStageScroll,
      scrollStageIntoView,
      scrollCardIntoView,
    }),
    [refs, getStageContainerRef, handleStageScroll, scrollStageIntoView, scrollCardIntoView]
  );
}
